import { StatusCodes } from 'http-status-codes';
import { call, CallEffect, delay, select, SelectEffect } from 'redux-saga/effects';
import { getApiClient } from 'b2b-apps/shop/common/api/axios';
import { RequestConfigType } from 'b2b-apps/shop/common/utils/api/RequestConfig';
import { isServer } from 'b2b-apps/shop/common/utils/env';
import { getActiveLanguage } from 'b2b-apps/shop/i18n/redux/selectors';
import { LANGUAGE_CODE } from 'b2b-common/core/i18n/consts';

const MAX_TRIES = 3;
const RETRY_DELAY = 3 * 1000;
const RETRY_CODES = [
  StatusCodes.INTERNAL_SERVER_ERROR,
  StatusCodes.BAD_GATEWAY,
  StatusCodes.SERVICE_UNAVAILABLE,
  StatusCodes.GATEWAY_TIMEOUT,
];
const SERVER_SIDE_TIMEOUT = 10 * 1000;

function* requestApiSaga(
  requestConfig: RequestConfigType,
): Generator<
  CallEffect | SelectEffect,
  CallEffect,
  CallEffect & LANGUAGE_CODE
> {
  const {
    withLocalization = true,
    ...config
  } = requestConfig;

  if (isServer) {
    config.timeout = SERVER_SIDE_TIMEOUT;
  }

  const language = withLocalization ? yield select(getActiveLanguage) : null;

  return yield call(
    getApiClient(language).request,
    { ...config },
  );
}

function* retryRequestApiSaga(
  config: RequestConfigType,
): Generator<CallEffect, CallEffect | undefined, CallEffect> {
  for (let requestNumber = 0; requestNumber < MAX_TRIES; requestNumber++) {
    try {
      return yield call(requestApiSaga, config);
    } catch (error: any) {
      const statusCode = error?.response?.status;

      if (RETRY_CODES.includes(statusCode) && requestNumber < MAX_TRIES - 1) {
        yield delay(RETRY_DELAY);
        continue;
      }

      throw error;
    }
  }
}

export { requestApiSaga, retryRequestApiSaga };
